import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TagDto } from '@picflow/types';
import { Observable, catchError, of, tap } from 'rxjs';
import { AppConfigService } from './providers/config.service';
import { DataLoaderService } from './services/data-loader.service';
import { GetAllService } from './services/get-all.service';
import { ObservableDataLoaderService } from './services/observable-data-loader.service';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private getAllService = inject(GetAllService);

  public get tags() {
    const url = `${this.config.apiEndpoint}/tags`;

    return this.observableLoader.get(
      'tags',
      () =>
        this.getAllService.getAll<TagDto>(url).pipe(
          catchError(err => {
            console.error(err);
            // Returning an empty array here will prevent the loader from trying to load the data again
            return of([]) as Observable<TagDto[]>;
          })
        ),
      1000 * 60 * 2
    );
  }

  public createTag(name: string, category: string) {
    return this.http.post<TagDto>(`${this.config.apiEndpoint}/tags`, { name, category }).pipe(
      tap(resp => {
        this.observableLoader.update<TagDto[]>('tags', tags => {
          if (!tags) return;
          return [...tags, resp];
        });
      })
    );
  }

  public updateTag(tagId: string, name: string, category: string) {
    return this.http.patch<TagDto>(`${this.config.apiEndpoint}/tags/${tagId}`, { name, category }).pipe(
      tap(resp => {
        this.observableLoader.update<TagDto[]>('tags', tags =>
          (tags || []).map(tag => (tag.id === tagId ? resp : tag))
        );
      })
    );
  }

  public deleteTag(tagId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/tags/${tagId}`).pipe(
      tap(() => {
        this.observableLoader.update<TagDto[]>('tags', tags => (tags || []).filter(tag => tag.id !== tagId));
        this.loader.remove('assets');
      })
    );
  }
}
